// src/pages/subscription-suspended/index.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import api from "@/lib/api";

function formatDt(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("ro-RO");
}

export default function SubscriptionSuspendedPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { businesses, currentBusinessId, roles, roleDetails, logout } = useAuth();

  const biz = useMemo(() => {
    const id = currentBusinessId ? String(currentBusinessId) : null;
    return (businesses || []).find((b) => String(b.id) === id) || null;
  }, [businesses, currentBusinessId]);

  const isSuperAdmin = roles?.includes("Super Administrator");

  const isOwnerLike = useMemo(() => {
    if (isSuperAdmin) return true;
    const bid = currentBusinessId ? Number(currentBusinessId) : null;
    if (!bid) return false;
    const br = roleDetails?.business_roles || [];
    return br.some((x) => x.business_id === bid && x.role === "owner");
  }, [isSuperAdmin, roleDetails, currentBusinessId]);

  const subStatus = String(biz?.subscription_status || "").toLowerCase();
  const periodEnd = biz?.current_period_end || null;
  const graceUntil = biz?.grace_until || null;

  const intervalRef = useRef(null);

  const [checking, setChecking] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [message, setMessage] = useState(null); // { type: "info"|"error"|"success", text: string }

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const checkNow = async () => {
    setChecking(true);
    setMessage(null);

    try {
      const { data } = await api.get("/auth/me");
      const list = data?.businesses || [];
      const id = currentBusinessId ? String(currentBusinessId) : null;
      const fresh = (list || []).find((b) => String(b.id) === id) || null;

      const st = String(fresh?.subscription_status || "").toLowerCase();

      // dacă nu mai e blocked/canceled/past_due => e ok
      if (st && st !== "blocked" && st !== "canceled" && st !== "past_due") {
        stopPolling();
        setMessage({
          type: "success",
          text: t("subscription.activeSuccess"),
        });

        // hard redirect ca să se re-bootstrap-eze AuthProvider (/auth/me)
        window.location.href = "/app";
        return;
      }

      setMessage({
        type: "info",
        text: t("subscription.stillSuspended"),
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || t("subscription.verifyError");
      setMessage({ type: "error", text: String(detail) });
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    checkNow();

    stopPolling();
    intervalRef.current = setInterval(checkNow, 15000);

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusinessId]);

  const openBilling = () => {
    navigate("/app/billing", { replace: true, state: { from: location.pathname } });
  };

  const tryOpenPortal = async () => {
    setOpeningPortal(true);
    setMessage(null);

    try {
      const { data } = await api.post("/billing/portal", {});
      const url = data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      setMessage({ type: "error", text: "Nu am primit link-ul către portal." });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.stripe?.[0];
      setMessage({
        type: "error",
        text: detail || t("billing.cannotOpenPortal"),
      });
      openBilling();
    } finally {
      setOpeningPortal(false);
    }
  };

  const msgBoxClass =
    message?.type === "success"
      ? "border-green-400/20 bg-green-400/10 text-green-100"
      : message?.type === "error"
      ? "border-red-400/20 bg-red-400/10 text-red-100"
      : "border-white/10 bg-black/20 text-white/70";

  return (
    <div className="min-h-screen bg-[#070B14] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-white/5 p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{t("subscription.suspended")}</h1>
            <p className="text-white/70 mt-2">
              {t("subscription.blockedDesc")}
            </p>
          </div>

          <button
            onClick={logout}
            className="text-sm px-3 py-2 rounded-xl border border-white/15 hover:bg-white/10"
          >
            {t("subscription.logoutButton")}
          </button>
        </div>

        <div className="mt-5 rounded-xl border border-white/10 bg-black/20 p-4 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <div className="text-white/60">Business</div>
              <div className="font-medium">{biz?.name || "—"}</div>
            </div>
            <div>
              <div className="text-white/60">{t("common.status")}</div>
              <div className="font-medium">{subStatus || "—"}</div>
            </div>
            <div>
              <div className="text-white/60">{t("subscription.expiredAt")}</div>
              <div className="font-medium">{formatDt(periodEnd)}</div>
            </div>
            <div>
              <div className="text-white/60">{t("subscription.graceUntil")}</div>
              <div className="font-medium">{formatDt(graceUntil)}</div>
            </div>
          </div>
        </div>

        {!isOwnerLike ? (
          <div className="mt-5 rounded-xl border border-yellow-400/20 bg-yellow-400/10 p-4 text-sm text-yellow-100">
            {t("subscription.noPermission")}
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            onClick={checkNow}
            disabled={checking}
            className="text-sm px-3 py-2 rounded-xl border border-white/15 hover:bg-white/10 disabled:opacity-50"
          >
            {checking ? t("subscription.checking") : t("subscription.checkStatus")}
          </button>
        </div>

        {message ? (
          <div className={`mt-4 rounded-xl border p-3 text-sm ${msgBoxClass}`}>{message.text}</div>
        ) : null}

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            disabled={!isOwnerLike || openingPortal}
            onClick={tryOpenPortal}
            className="flex-1 px-4 py-3 rounded-xl bg-white text-black font-medium disabled:opacity-50"
          >
            {openingPortal ? t("subscription.openingPortal") : t("subscription.openPortal")}
          </button>

          <button
            disabled={!isOwnerLike}
            onClick={openBilling}
            className="flex-1 px-4 py-3 rounded-xl border border-white/15 hover:bg-white/10 disabled:opacity-50"
          >
            {t("subscription.viewPlans")}
          </button>
        </div>

        <div className="mt-5 text-sm text-white/60">
          {t("subscription.helpText")}{" "}
          <a className="underline" href="mailto:support@appointiq.ro">
            support@appointiq.ro
          </a>
          .
        </div>

        <div className="mt-4 text-xs text-white/40">
          {t("subscription.noteText")}
        </div>
      </div>
    </div>
  );
}
